import { recordAgentEvent } from '@extension/wa-communication-layer/api/api-calls';

/**
 * Agent / bot product-event reporter.
 *
 * The one choke point for the bots/agents observability events. Every call routes to
 * the DURABLE backend ingest (`recordAgentEvent` → `POST /actions?cmd=record-agent-event`
 * → the `events` bus), which works in BOTH the web app and the extension/content-script
 * context — unlike `AnalyticsService.event()`, whose `gtag` path is a no-op in the
 * extension (so the new `/agents` UI emitted nothing). GA/gtag marketing events are kept
 * as-is at the call sites; this ADDS the durable path.
 *
 * Contract (MANDATORY):
 *  - every event carries `platform` ('claude' for the new `/agents` surfaces, 'gemini'
 *    for the legacy bot/KB surfaces);
 *  - `agentId` is included where available;
 *  - NO chatId (raw OR hashed) is ever sent from the client — chatId belongs only to
 *    server-emitted interaction events, and the backend does its own server-side HMAC.
 *    This reporter DROPS any `chatId`/`chatIdHash` prop defensively.
 *  - NO message bodies ever go into props.
 */

export type AgentPlatform = 'claude' | 'gemini';

/** Props a caller may pass. chatId/chatIdHash and bodies are never accepted (dropped). */
export interface AgentEventProps {
  agentId?: string | null;
  [key: string]: unknown;
}

/** Props the client must never emit (server-only / privacy). Dropped defensively. */
const FORBIDDEN_PROPS = new Set(['chatId', 'chatIdHash']);

/**
 * Report one agent/bot product event to the durable backend bus.
 *
 * Builds `{ platform, ...props }`, drops `undefined`/`null` and any forbidden prop
 * (`chatId`/`chatIdHash`), and fires `recordAgentEvent`. Fire-and-forget: a failed
 * ingest never throws into the caller (telemetry must not break a user action).
 */
export function reportAgentEvent(
  name: string,
  platform: AgentPlatform,
  props?: AgentEventProps,
): void {
  try {
    const out: Record<string, unknown> = { platform };
    if (props) {
      for (const [key, value] of Object.entries(props)) {
        if (FORBIDDEN_PROPS.has(key)) continue; // chatId never leaves the client
        if (value === undefined || value === null) continue;
        out[key] = value;
      }
    }
    void recordAgentEvent(name, out).catch(() => undefined);
  } catch {
    /* telemetry is best-effort — never break the user action */
  }
}
